/**
 * Schedule Tab Component for Client Detail Page
 * Displays client appointments on a calendar and list view
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Clock, User, MapPin, Phone, List, CalendarDays } from "lucide-react";
import { ScheduleCalendar } from "@/components/ScheduleCalendar";

export function ScheduleTab({ 
  schedule, 
  loading, 
  onRefresh 
}) {
  const [viewMode, setViewMode] = useState("calendar");
  const { 
    appointments = [], 
    assigned_caregivers = [], 
    total_scheduled = 0, 
    total_completed = 0 
  } = schedule;

  return (
    <div data-testid="client-schedule-tab">
      <div className="form-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">
              Client Schedule
            </h3>
            <p className="text-sm text-slate-500">
              All appointments and visits for this client
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                {total_scheduled} Scheduled
              </span>
              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                {total_completed} Completed
              </span>
            </div>
            <div className="flex items-center border rounded-lg overflow-hidden">
              <Button
                size="sm"
                variant={viewMode === "calendar" ? "default" : "ghost"}
                onClick={() => setViewMode("calendar")}
                className="rounded-none gap-1"
                data-testid="calendar-view-btn"
              >
                <CalendarDays className="w-4 h-4" />
                Calendar
              </Button>
              <Button
                size="sm"
                variant={viewMode === "list" ? "default" : "ghost"}
                onClick={() => setViewMode("list")}
                className="rounded-none gap-1"
                data-testid="list-view-btn"
              >
                <List className="w-4 h-4" />
                List
              </Button>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={onRefresh}
              className="gap-2"
              data-testid="refresh-schedule-btn"
            >
              <Clock className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Assigned Caregivers Section */}
        {assigned_caregivers?.length > 0 && (
          <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Assigned Caregivers
            </h4>
            <div className="flex flex-wrap gap-3">
              {assigned_caregivers.map((caregiver) => (
                <div
                  key={caregiver.id}
                  className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200"
                  data-testid={`assigned-caregiver-${caregiver.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{caregiver.name}</p>
                    <p className="text-xs text-slate-500">{caregiver.role}</p>
                  </div>
                  {caregiver.phone && (
                    <a
                      href={`tel:${caregiver.phone}`}
                      className="ml-2 text-primary hover:text-primary/80"
                    >
                      <Phone className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-slate-500">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-50 animate-pulse" />
            <p className="text-sm">Loading schedule...</p>
          </div>
        ) : appointments?.length === 0 ? (
          <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No appointments scheduled yet</p>
            <p className="text-xs mt-1">
              Appointments will appear here when scheduled from the Schedule page
            </p>
          </div>
        ) : viewMode === "calendar" ? (
          /* Calendar View */
          <ScheduleCalendar 
            appointments={appointments}
            loading={loading}
            showClientName={false}
            showEmployeeName={true}
          />
        ) : (
          /* List View */
          <div className="space-y-3">
            {appointments.map((appt) => (
              <div
                key={appt.id}
                className={`p-4 border rounded-lg transition-colors ${
                  appt.is_assessment
                    ? "bg-blue-50 border-blue-200 hover:border-blue-300"
                    : appt.status === "completed"
                    ? "bg-emerald-50 border-emerald-200 hover:border-emerald-300"
                    : appt.status === "cancelled"
                    ? "bg-red-50 border-red-200 hover:border-red-300"
                    : "bg-white border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`schedule-appt-${appt.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        appt.is_assessment
                          ? "bg-blue-100 text-blue-700"
                          : appt.appointment_type === "visit"
                          ? "bg-emerald-100 text-emerald-700"
                          : appt.appointment_type === "follow_up"
                          ? "bg-purple-100 text-purple-700"
                          : appt.appointment_type === "md_appointment"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-slate-100 text-slate-700"
                      }`}>
                        {appt.is_assessment ? "RN Assessment" : 
                          appt.appointment_type === "visit" ? "Home Visit" :
                          appt.appointment_type === "follow_up" ? "Follow Up" :
                          appt.appointment_type === "md_appointment" ? "MD Appointment" :
                          appt.appointment_type?.charAt(0).toUpperCase() + appt.appointment_type?.slice(1)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        appt.status === "scheduled"
                          ? "bg-yellow-100 text-yellow-700"
                          : appt.status === "completed"
                          ? "bg-emerald-100 text-emerald-700"
                          : appt.status === "cancelled"
                          ? "bg-red-100 text-red-700"
                          : "bg-slate-100 text-slate-700"
                      }`}>
                        {appt.status?.charAt(0).toUpperCase() + appt.status?.slice(1)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-700">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {appt.scheduled_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {appt.scheduled_time}
                      </span>
                      {appt.duration_minutes && (
                        <span className="text-slate-500">
                          ({Math.floor(appt.duration_minutes / 60)}h {appt.duration_minutes % 60}m)
                        </span>
                      )}
                    </div>

                    {appt.employee_name && (
                      <p className="text-sm text-slate-600 mt-2 flex items-center gap-1">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Caregiver:</span> {appt.employee_name}
                      </p>
                    )}

                    {appt.address && (
                      <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {appt.address}
                      </p>
                    )}

                    {appt.notes && (
                      <p className="text-sm text-slate-500 mt-2 italic border-l-2 border-slate-300 pl-2">
                        {appt.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
